/**
 * Studio v2 — the PURE static HTML renderer (foundation-spec §6.2).
 *
 * `synthesizeArtifactHtml(content, resolved)` — the `synthesizeReportHtml`
 * analogue. Serializes `resolved.tokens` into a `:root {…}` block, inlines
 * `base.css`, adds the font `<link>`s + `layoutCss`, and renders typed blocks to
 * token-driven HTML (reading `var(--xv-art-*)`, unlike email which substitutes
 * values). Used for thumbnails, download/export, and the publish-to-R2 HTML.
 *
 * Pure string renderer — no React, no DOM. Serves slides (paged) + doc (single).
 */
import { escapeHtml, escapeAttr, safeHref, safeCssTokenValue, sanitizeLayoutCss } from './escape.js';
import { BASE_CSS } from './base-css.js';
import { STUDIO_THEMES } from '../themes/index.js';
/** layoutCss of every code-defined built-in theme — trusted, injected verbatim. */
const BUILTIN_LAYOUT_CSS = new Set(Object.values(STUDIO_THEMES)
    .map((t) => t.layoutCss)
    .filter((css) => typeof css === 'string'));
/**
 * Resolve the `layoutCss` to inject. A built-in theme's block (matched by exact
 * content against the code-defined catalog) is trusted and passed through
 * verbatim; any other block is USER-SUPPLIED (a custom theme) and is sanitized so
 * it can't break out of the published `<style>` (`</style><script>…`), `@import`
 * remote CSS, or hide a payload in a comment.
 */
function resolveLayoutCss(layoutCss) {
    if (!layoutCss)
        return '';
    return BUILTIN_LAYOUT_CSS.has(layoutCss) ? layoutCss : sanitizeLayoutCss(layoutCss);
}
/** Render the `:root { … }` declaration block from the resolved token map. */
function renderRootVars(resolved) {
    const lines = Object.entries(resolved.tokens)
        .filter(([, v]) => v !== undefined)
        // Token values are user-controlled (brand kit / custom theme); a value like
        // `red;}</style><script>…` would break out of the published `<style>`.
        // Sanitize each — an unsafe value drops its declaration (token falls back to
        // its CSS default) while the surviving key order is preserved.
        .map(([k, v]) => [k, safeCssTokenValue(v)])
        .filter(([, v]) => v !== '')
        .map(([k, v]) => `  ${k}: ${v};`)
        .join('\n');
    return `:root {\n${lines}\n}`;
}
/** Wrap an inline run that highlights the first occurrence of `accent`. */
function highlightSplit(text, accent) {
    if (!accent)
        return escapeHtml(text);
    const idx = text.indexOf(accent);
    if (idx === -1)
        return escapeHtml(text);
    return (escapeHtml(text.slice(0, idx)) +
        `<span class="xv-art-highlight">${escapeHtml(accent)}</span>` +
        escapeHtml(text.slice(idx + accent.length)));
}
/** Minimal inline markdown → HTML with treatment classes (highlight/link/code). */
function renderInline(text) {
    // Apply inline transforms segment-by-segment around `[label](url)` links,
    // matching the link on the RAW text so the URL is HTML-escaped exactly once.
    // (Escaping the whole string first then matching would double-encode `&` in
    // the URL into `&amp;amp;`.) Non-link spans get the usual escape + markdown.
    const linkRe = /(\[[^\]]+\]\(https?:\/\/[^)]+\))/g;
    const linkPart = /^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/;
    return text
        .split(linkRe)
        .map((seg) => {
        const link = linkPart.exec(seg);
        if (link) {
            const [, label, url] = link;
            return `<a class="xv-art-link" href="${escapeAttr(url)}" target="_blank" rel="noopener">${escapeHtml(label)}</a>`;
        }
        let s = escapeHtml(seg);
        s = s.replace(/`([^`]+)`/g, '<code class="xv-art-code">$1</code>');
        s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
        return s;
    })
        .join('');
}
function blockTag(block, className, inner) {
    return `<div class="${className}" data-block-id="${escapeAttr(block.id)}">${inner}</div>`;
}
/**
 * `wm.logoUrl` is normally guarded by `safeHref` (http(s)/mailto/tel/relative
 * only — `data:` is rejected). A `data:` image URI is also accepted here —
 * but ONLY for this resolved slot, which by this point can only be either an
 * untouched brand-kit https(s) URL or a server-MATERIALIZED data URI (#5385,
 * `materializeBrandKitForAnonymousSurface` in apps/api) — never raw user
 * input (the brand-kit write path's `parseLogoUrl`/`safeHttpUrl` already
 * rejects a hand-pasted `data:` scheme before it is ever stored).
 */
function isInlineImageDataUri(url) {
    return /^data:image\//i.test(url);
}
function renderWordmark(content, resolved) {
    const wm = resolved.wordmark;
    const logoSrc = wm.logoUrl
        ? isInlineImageDataUri(wm.logoUrl)
            ? wm.logoUrl
            : safeHref(wm.logoUrl)
        : '';
    if (logoSrc) {
        return `<span class="xv-art-wordmark"><img src="${escapeAttr(logoSrc)}" alt="${escapeAttr(content?.alt ?? wm.text ?? 'logo')}" class="xv-art-wordmark__logo" /></span>`;
    }
    return `<span class="xv-art-wordmark">${escapeHtml(wm.text ?? 'Xavior')}</span>`;
}
function renderBlock(block, resolved) {
    if (block.visible === false)
        return '';
    const c = block.content;
    switch (block.type) {
        case 'eyebrow': {
            const e = c;
            const idx = e.index ? `<span class="xv-art-eyebrow__index">${escapeHtml(e.index)}</span> ` : '';
            return blockTag(block, 'xv-art-block xv-art-eyebrow', `${idx}${escapeHtml(e.label)}`);
        }
        case 'headline': {
            const h = c;
            const tagline = h.tagline ? `<p class="xv-art-headline__tagline">${escapeHtml(h.tagline)}</p>` : '';
            return blockTag(block, 'xv-art-block xv-art-headline', `<h1>${escapeHtml(h.title)}</h1>${tagline}`);
        }
        case 'title': {
            const t = c;
            const eyebrow = t.eyebrow ? `<p class="xv-art-eyebrow">${escapeHtml(t.eyebrow)}</p>` : '';
            const sub = t.subtitle ? `<p class="xv-art-title__sub">${escapeHtml(t.subtitle)}</p>` : '';
            return blockTag(block, 'xv-art-block xv-art-title', `${eyebrow}<h1>${escapeHtml(t.title)}</h1>${sub}`);
        }
        case 'summary': {
            const s = c;
            return blockTag(block, 'xv-art-block xv-art-summary', `<p>${renderInline(s.text)}</p>`);
        }
        case 'statement': {
            const s = c;
            const attr = s.attribution ? `<p class="xv-art-statement__attr">${escapeHtml(s.attribution)}</p>` : '';
            return blockTag(block, 'xv-art-block xv-art-statement', `<p>${renderInline(s.text)}</p>${attr}`);
        }
        case 'prose': {
            const p = c;
            const heading = p.heading ? `<h2>${escapeHtml(p.heading)}</h2>` : '';
            return blockTag(block, 'xv-art-block xv-art-prose', `${heading}<p>${renderInline(p.text)}</p>`);
        }
        case 'stat': {
            const s = c;
            const square = s.accentShape === 'square' ? '<span class="xv-art-stat__square"></span>' : '';
            const subhead = s.subhead
                ? `<p class="xv-art-stat__subhead">${highlightSplit(s.subhead, s.subheadAccent)}</p>`
                : '';
            return blockTag(block, 'xv-art-block xv-art-stat', `<div class="xv-art-stat__value">${square}<span>${highlightSplit(s.value, s.valueAccent)}</span></div>${subhead}`);
        }
        case 'stats-band': {
            const sb = c;
            const items = (sb.stats ?? [])
                .map((s) => {
                const trend = s.trend === 'up' ? ' trend-up' : s.trend === 'down' ? ' trend-down' : '';
                const tv = s.trendValue ? ` <span class="xv-art-stat__trend">${escapeHtml(s.trendValue)}</span>` : '';
                return `<div class="xv-art-stats-band__item${trend}"><span class="xv-art-stat__num">${escapeHtml(s.value)}${tv}</span><span class="xv-art-stat__label">${escapeHtml(s.label)}</span></div>`;
            })
                .join('');
            return blockTag(block, 'xv-art-block xv-art-stats-band', items);
        }
        case 'bullets': {
            const b = c;
            const heading = b.heading ? `<p class="xv-art-bullets__heading">${escapeHtml(b.heading)}</p>` : '';
            const items = (b.items ?? []).map((it) => `<li>${renderInline(it.text)}</li>`).join('');
            return blockTag(block, 'xv-art-block xv-art-bullets', `${heading}<ul>${items}</ul>`);
        }
        case 'callout': {
            const co = c;
            const heading = co.heading ? `<strong class="xv-art-callout__heading">${escapeHtml(co.heading)}</strong> ` : '';
            return blockTag(block, `xv-art-block xv-art-callout xv-art-callout--${escapeAttr(co.style)}`, `${heading}${renderInline(co.text)}`);
        }
        case 'quote': {
            const q = c;
            const attr = q.attribution
                ? `<footer class="xv-art-quote__attr">— ${escapeHtml(q.attribution)}${q.role ? `, ${escapeHtml(q.role)}` : ''}</footer>`
                : '';
            return blockTag(block, 'xv-art-block xv-art-quote', `<blockquote>${escapeHtml(q.text)}</blockquote>${attr}`);
        }
        case 'cta': {
            const cta = c;
            const body = cta.body ? `<p class="xv-art-cta__body">${escapeHtml(cta.body)}</p>` : '';
            let action = '';
            if (cta.actionUrl && cta.actionLabel) {
                // Scheme-guard the URL — an unsafe `actionUrl` (javascript:/data:/…) drops
                // the anchor and the label renders as plain text instead.
                const href = safeHref(cta.actionUrl);
                action = href
                    ? `<a class="xv-art-cta__btn" href="${escapeAttr(href)}">${escapeHtml(cta.actionLabel)}</a>`
                    : escapeHtml(cta.actionLabel);
            }
            return blockTag(block, 'xv-art-block xv-art-cta', `<h2>${escapeHtml(cta.headline)}</h2>${body}${action}`);
        }
        case 'image': {
            const img = c;
            const caption = img.caption ? `<p class="xv-art-image__caption">${escapeHtml(img.caption)}</p>` : '';
            return blockTag(block, 'xv-art-block xv-art-image', `<img src="${escapeAttr(safeHref(img.src))}" alt="${escapeAttr(img.alt)}" />${caption}`);
        }
        case 'faq': {
            const f = c;
            const items = (f.items ?? [])
                .map((it) => `<details><summary>${escapeHtml(it.question)}</summary><p>${renderInline(it.answer)}</p></details>`)
                .join('');
            return blockTag(block, 'xv-art-block xv-art-faq', items);
        }
        case 'sources': {
            const s = c;
            const items = (s.items ?? [])
                .map((it) => {
                const link = it.url && /^https?:\/\//i.test(it.url)
                    ? `<a class="xv-art-link" href="${escapeAttr(it.url)}" target="_blank" rel="noopener">${escapeHtml(it.title)}</a>`
                    : escapeHtml(it.title);
                const desc = it.description ? ` — ${renderInline(it.description)}` : '';
                return `<li>${link}${desc}</li>`;
            })
                .join('');
            return blockTag(block, 'xv-art-block xv-art-sources', `<h3>Sources</h3><ul>${items}</ul>`);
        }
        case 'wordmark':
            return blockTag(block, 'xv-art-block xv-art-wordmark-block', renderWordmark(c, resolved));
        case 'footer': {
            const f = c;
            const meta = f.generatedAt ? `<span class="xv-art-footer__meta">Generated ${escapeHtml(f.generatedAt)}</span>` : '';
            return blockTag(block, 'xv-art-block xv-art-footer', `${renderWordmark(undefined, resolved)} ${meta}`);
        }
        case 'two-column': {
            const tc = c;
            const left = (tc.left ?? []).map((b) => renderBlock(b, resolved)).join('');
            const right = (tc.right ?? []).map((b) => renderBlock(b, resolved)).join('');
            const ratio = (tc.ratio ?? '1:1').replace(':', '-');
            return blockTag(block, `xv-art-block xv-art-two-column xv-art-two-column--${ratio}`, `<div class="xv-art-col">${left}</div><div class="xv-art-col">${right}</div>`);
        }
        case 'chart':
        case 'table': {
            const tc = c;
            const caption = tc.caption ? `<p class="xv-art-image__caption">${escapeHtml(tc.caption)}</p>` : '';
            return blockTag(block, `xv-art-block xv-art-${block.type}`, caption);
        }
        default:
            return '';
    }
}
function renderPage(page, resolved, shape) {
    if (page.visible === false)
        return '';
    const blocks = [...(page.blocks ?? [])].sort((a, b) => a.order - b.order);
    const inner = blocks.map((b) => renderBlock(b, resolved)).join('\n');
    const pageClass = shape === 'paged' ? 'xv-art-page xv-art-page--slide' : 'xv-art-page xv-art-page--doc';
    return `<section class="${pageClass}" data-page-id="${escapeAttr(page.id)}">${inner}</section>`;
}
/**
 * Render a self-contained static HTML document for an artifact (slides + doc).
 * The `synthesizeReportHtml` analogue: tokens → `:root`, base.css inlined, blocks
 * rendered as token-driven markup reading `var(--xv-art-*)`.
 */
export function synthesizeArtifactHtml(content, resolved) {
    const pages = [...(content.pages ?? [])].sort((a, b) => a.order - b.order);
    const body = pages.map((p) => renderPage(p, resolved, content.shape)).join('\n');
    const rootVars = renderRootVars(resolved);
    const fontLinks = resolved.fontUrls
        // Brand-kit / theme `fontsUrl` is user-controlled — scheme-guard the stylesheet
        // <link href> and drop any URL that isn't a safe scheme.
        .map((u) => safeHref(u))
        .filter((u) => u)
        .map((u) => `  <link href="${escapeAttr(u)}" rel="stylesheet" crossorigin="anonymous" />`)
        .join('\n');
    const safeLayoutCss = resolveLayoutCss(resolved.theme.layoutCss);
    const layoutCss = safeLayoutCss ? `\n/* theme layoutCss */\n${safeLayoutCss}` : '';
    const rootClass = content.shape === 'paged' ? 'xv-art xv-art--paged' : 'xv-art xv-art--single';
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(content.title)}</title>
  <meta name="generator" content="XAVIOR Studio" />
  <link rel="preconnect" href="https://fonts.googleapis.com" crossorigin="anonymous" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
${fontLinks}
  <style>
/* Published-canvas reset (#5204): the theme owns the WHOLE canvas — no UA body
   margin (which framed dark pages in white) and the page background extends
   below the fold, so the measure column reads centered on a uniform canvas. */
html, body { margin: 0; padding: 0; background: var(--xv-art-bg, #ffffff); }
${rootVars}
${BASE_CSS}${layoutCss}
  </style>
</head>
<body>
  <div class="${rootClass}" data-xv-theme="${escapeAttr(resolved.theme.id)}" data-xv-format="${escapeAttr(content.format)}">
${body}
  </div>
</body>
</html>`;
}
//# sourceMappingURL=static.js.map